import {observable, action} from 'mobx'
import {notification} from 'antd'

class Data {
  @observable loading = false
  @observable loaded = false
  @observable data = []
  @observable pagination = {}
}

export class Subscription {
  @observable plans = new Data()
  @observable cards = new Data()
  @observable subscriptions = new Data()
  @observable invoices = new Data()
  @observable loading = false

  constructor(rootStore, api) {
    this.root = rootStore
    this.api = api
  }

  @action async getPlans() {
    this.plans.loading = true
    this.plans.data = []
    const {isLoggedIn} = this.root.auth
    try {
      const requestFunc = (url, method) => isLoggedIn ? this.api.request(url, method) : this.api.req(url, method)
      const {results} = await requestFunc('billing/plans', 'get')
      if (results) {
        this.plans.data = results.concat()
      }
    } catch (e) {
      notification['error']({
        message: 'Failed to get a list of subscription plans',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.plans.loading = false
    }
  }

  @action async getCards() {
    this.cards.loading = true
    this.cards.data = []
    try {
      const {results} = await this.api.request('user/billing/cards', 'get')
      if (results) {
        this.cards.data = results.concat()
      }
      this.cards.loaded = true
    } catch (e) {
      notification['error']({
        message: 'Failed to get a list of your cards',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.cards.loading = false
    }
  }

  @action async deleteCard(id) {
    this.cards.loading = true
    const body = {id}
    try {
      const {results} = await this.api.request('user/billing/cards', 'delete', body)
      if (results) {
        this.cards = results.concat()
      }
    } catch (e) {
      notification['error']({
        message: 'Failed to delete payment method',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.cards.loading = false
    }
  }

  @action async addCard(stripe_token) {
    this.cards.loading = true
    const body = {stripe_token}
    try {
      const {results} = await this.api.request('user/billing/cards', 'post', body)
      if (results) {
        this.cards = results.concat()
      }
      await this.getCards()
    } catch (e) {
      notification['error']({
        message: 'Failed to add card',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.cards.loading = false
    }
  }

  @action async getSubscriptions() {
    this.subscriptions.loading = true
    this.subscriptions.data = []
    try {
      const {results} = await this.api.request('user/billing/subscriptions', 'get')
      if (results) {
        this.subscriptions.data = results.concat()
      }
      this.subscriptions.loaded = true
    } catch (e) {
      notification['error']({
        message: 'Failed to get subscriptions',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.subscriptions.loading = false
    }
  }

  @action async getInvoices(page = 1) {
    this.invoices.loading = true
    this.invoices.data = []
    try {
      const {results, count} = await this.api.request(`user/billing/invoices?page=${page}`, 'get')
      if (results) {
        this.invoices.data = results.concat()
      }
      this.invoices.pagination.page = page
      if (page === 1) {
        this.invoices.pagination.size = results.length
      }
      this.invoices.pagination.count = count
      this.invoices.loaded = true
    } catch (e) {
      notification['error']({
        message: 'Failed to get a list of your invoices',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.invoices.loading = false
    }
  }

  @action async subscribe(card_token) {
    this.loading = true
    const plan = this.root.user.selectedPlan || this.root.user.planSettings[0].id
    const body = {
      card_token,
      plan
    }
    try {
      await this.api.request('user/billing/subscriptions', 'post', body)
      await this.root.user.getUserData(false)
      this.root.redirectTo({pathname:'/'})
    } catch (e) {
      notification['error']({
        message: 'Failed to subscribe',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.loading = false
    }
  }

  @action async changePlan() {
    this.plans.loading = true
    const body = {plan:this.selectedPlan}
    try {
      await this.api.request('user/billing/subscriptions', 'post', body)
      await this.getPlans()
    } catch (e) {
      notification['error']({
        message: 'Failed to change subscription plan',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.plans.loading = false
    }
  }

  @action async unsubscribe() {
    this.loading = true
    try {
      await this.api.request('user/billing/subscriptions', 'delete')
      await this.root.user.getUserData(false)
      notification['success']({
        message: 'Unsubscribed successfully'
      })
    } catch (e) {
      notification['error']({
        message: 'Failed to unsubscribe',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.loading = false
    }
  }
}
